import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import { Box, FlexBox } from "../../components/primitives";
import { SecondaryButton } from "../../components/common/common.style";
import { loadOpenCV } from "../../opencs-loader";
import { useMatchCardMutation } from "../../services/myServer/cardVector";
import { CardSelected, StatsDataType } from "../../type";
import { useCards } from "../../context/CardsContext";
import { CardName } from "../../components/Cards/Cards.styled";
import { CardTraderLanguageValueType } from "magic-shared-types";
// import magicImg from "../../assets/magic.jpg";

interface SimpleCameraCaptureProps {
  setIsCameraUploadOpen: (arg1: boolean) => void;
  defaultStatsValue: StatsDataType;
  setCardsBoard: React.Dispatch<React.SetStateAction<CardSelected[]>>;
}

type MatchResultType = {
  id: number;
  gameId: 1;
  score: number;
  collectorNumber: string;
  name: string;
  imageUrl: string;
  blue_print_id: number;
  language: string;
  editionCode: string;
};

export const TestPicture: React.FC<SimpleCameraCaptureProps> = ({
  setIsCameraUploadOpen,
  setCardsBoard,
  defaultStatsValue,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const tmpCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [matchResult, setMatchResult] = useState<MatchResultType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [matchCard] = useMatchCardMutation();
  const cardComplexArray = useCards();

  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cvReady, setCvReady] = useState(false);
  const [fullImage, setFullImage] = useState<string | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [lastRect, setLastRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // Load OpenCV
  useEffect(() => {
    loadOpenCV().then(() => setCvReady(true));
  }, []);

  // ✅ Add matched card to board
  useEffect(() => {
    if (!matchResult) return;
    setCardsBoard((prev) => [
      ...prev,
      {
        uid: uuidv4(),
        id: matchResult.id,
        game_id: 1,
        name: matchResult.name,
        ...defaultStatsValue,
        language: matchResult.language as CardTraderLanguageValueType,
      },
    ]);
    toast.success("Card uploaded successfully", { duration: 3000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchResult]);

  // Start camera
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setIsCameraReady(true);
        }
      } catch (err) {
        console.error("Camera error:", err);
      }
    };
    startCamera();
    return () => {
      videoRef.current?.srcObject &&
        (videoRef.current.srcObject as MediaStream)
          .getTracks()
          .forEach((t) => t.stop());
    };
  }, []);

  const detectCardRect = (canvas: HTMLCanvasElement) => {
    const cv = (window as any).cv;
    const src = cv.imread(canvas);
    if (src.cols === 0 || src.rows === 0) return null;

    const gray = new cv.Mat();
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    const thresh = new cv.Mat();
    cv.threshold(gray, thresh, 0, 255, cv.THRESH_BINARY_INV + cv.THRESH_OTSU);
    const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
    cv.morphologyEx(thresh, thresh, cv.MORPH_CLOSE, kernel);

    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(
      thresh,
      contours,
      hierarchy,
      cv.RETR_EXTERNAL,
      cv.CHAIN_APPROX_SIMPLE
    );

    let bestRect = null;
    let bestArea = 0;
    const MIN_AREA = 15000;
    const MAX_AREA = canvas.width * canvas.height * 0.5;
    const CARD_ASPECT = 63 / 88;
    const TOLERANCE = 0.2;

    for (let i = 0; i < contours.size(); i++) {
      const cnt = contours.get(i);
      const rect = cv.boundingRect(cnt);
      const area = rect.width * rect.height;
      const aspect = rect.width / rect.height;

      if (
        area >= MIN_AREA &&
        area <= MAX_AREA &&
        aspect > CARD_ASPECT * (1 - TOLERANCE) &&
        aspect < CARD_ASPECT * (1 + TOLERANCE) &&
        area > bestArea
      ) {
        bestArea = area;
        bestRect = rect;
      }
      cnt.delete();
    }

    src.delete();
    gray.delete();
    thresh.delete();
    kernel.delete();
    contours.delete();
    hierarchy.delete();
    return bestRect;
  };

  const captureFrame = async () => {
    if (!isCameraReady || !cvReady || !videoRef.current) return;
    const video = videoRef.current;

    // Full image
    const fullCanvas = document.createElement("canvas");
    fullCanvas.width = video.videoWidth;
    fullCanvas.height = video.videoHeight;
    fullCanvas.getContext("2d")!.drawImage(video, 0, 0);
    setFullImage(fullCanvas.toDataURL("image/png"));

    // Cropped card
    if (!tmpCanvasRef.current) {
      tmpCanvasRef.current = document.createElement("canvas");
      tmpCanvasRef.current.width = video.videoWidth;
      tmpCanvasRef.current.height = video.videoHeight;
      // tmpCanvasRef.current.width = 320;
      // tmpCanvasRef.current.height =
      //  (video.videoHeight / video.videoWidth) * 320;
    }
    const tmpCanvas = tmpCanvasRef.current;
    const tmpCtx = tmpCanvas.getContext("2d")!;
    tmpCtx.drawImage(video, 0, 0, tmpCanvas.width, tmpCanvas.height);

    const rect = detectCardRect(tmpCanvas);
    if (!rect) return;

    const scaleX = video.videoWidth / tmpCanvas.width;
    const scaleY = video.videoHeight / tmpCanvas.height;

    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = rect.width * scaleX;
    cropCanvas.height = rect.height * scaleY;
    cropCanvas
      .getContext("2d")!
      .drawImage(
        video,
        rect.x * scaleX,
        rect.y * scaleY,
        rect.width * scaleX,
        rect.height * scaleY,
        0,
        0,
        rect.width * scaleX,
        rect.height * scaleY
      );
    setCroppedImage(cropCanvas.toDataURL("image/png"));

    setIsLoading(true);
    try {
      const blob: Blob = await new Promise((res) =>
        fullCanvas.toBlob((b) => res(b!), "image/png", 0.8)
      );
      const formData = new FormData();
      formData.append("image", blob, "frame.jpg");
      const response = await matchCard(formData).unwrap();

      if (!response?.bestMatch) {
        alert(JSON.stringify(`❌ Card not found`));
        return;
      }
      const predictedCard = response.bestMatch.blue_print_id;
      const card = cardComplexArray[predictedCard];
      console.log(card);
      if (response.bestMatch) {
        setMatchResult({
          id: predictedCard,
          gameId: 1,
          score: response.bestMatch.similarity,
          collectorNumber: response.bestMatch.collector_number,
          name: card.name,
          imageUrl: response.bestMatch.image,
          blue_print_id: predictedCard,
          language: response.language,
          editionCode: card.editionCode,
        });
      }
      setIsLoading(false);
    } catch (e) {
      alert(JSON.stringify(`❌ Card not found ${e}`));
      setIsLoading(false);
    }
  };

  // Draw green rectangle overlay
  useEffect(() => {
    if (!isCameraReady || !cvReady) return;
    const ctxCanvas = canvasRef.current!;
    const video = videoRef.current!;

    const loop = () => {
      if (video.videoWidth && video.videoHeight) {
        ctxCanvas.width = video.videoWidth;
        ctxCanvas.height = video.videoHeight;
        const ctx = ctxCanvas.getContext("2d")!;
        ctx.clearRect(0, 0, ctxCanvas.width, ctxCanvas.height);

        if (tmpCanvasRef.current) {
          const rect = detectCardRect(tmpCanvasRef.current);
          if (rect) {
            const scaleX = video.videoWidth / tmpCanvasRef.current.width;
            const scaleY = video.videoHeight / tmpCanvasRef.current.height;
            setLastRect({
              x: rect.x * scaleX,
              y: rect.y * scaleY,
              width: rect.width * scaleX,
              height: rect.height * scaleY,
            });
          }
        }

        if (lastRect) {
          ctx.strokeStyle = "#16a34a"; // green
          ctx.lineWidth = 3;
          ctx.strokeRect(
            lastRect.x,
            lastRect.y,
            lastRect.width,
            lastRect.height
          );
        }
      }
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }, [isCameraReady, cvReady, lastRect]);

  return (
    <Box
      css={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: 10002,
        background: "black",
        overflow: "auto",
      }}
    >
      <div style={{ position: "relative", maxWidth: 400 }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ width: "100%", borderRadius: 8 }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />
      </div>

      {matchResult && (
        <FlexBox
          css={{
            borderRadius: "12px",
            padding: "8px",
            backgroundColor: "#fff",
            gap: 10,
            alignItems: "center",
            margin: "0px 8px",
          }}
        >
          <Box
            css={{
              width: "50px",
              height: "50px",
              backgroundColor: "#333",
              backgroundSize: "160%",
              backgroundPosition: "78% 17%",
              cursor: "pointer",
              zIndex: 10,
              backgroundImage: `url(${matchResult.imageUrl})`,
            }}
          />
          <Box>
            <CardName>{matchResult.name}</CardName>
            <Box css={{ fontSize: 14, color: "black" }}>
              #{matchResult.id}-{matchResult.editionCode}-
              {matchResult.collectorNumber}{" "}
              {matchResult?.score?.toFixed(3) || 100}
            </Box>
          </Box>
        </FlexBox>
      )}

      {isLoading ? (
        <Box css={{ color: "red", marginTop: 10 }}>Loading....</Box>
      ) : (
        <FlexBox style={{ gap: 10, marginTop: 10 }}>
          <SecondaryButton onClick={captureFrame}>Capture Card</SecondaryButton>
          <SecondaryButton onClick={() => setIsCameraUploadOpen(false)}>
            Close
          </SecondaryButton>
        </FlexBox>
      )}

      {fullImage && croppedImage && (
        <FlexBox style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}>
          <Box>
            <p>Full Frame:</p>
            <img src={fullImage} style={{ maxWidth: 200 }} />
          </Box>
          <Box>
            <p>Cropped Card:</p>
            <img src={croppedImage} style={{ maxWidth: 200 }} />
          </Box>
        </FlexBox>
      )}
    </Box>
  );
};
